import { linePlanningTool } from "./line-planning/index.js";
import { lineworkTool } from "./linework/index.js";
import { overlayManagerTool } from "./overlay-manager/index.js";
import { globalLayersTool } from "./global-layers/index.js";
import { projectionManagerTool } from "./projection-manager/index.js";
import { etopoSoundingTool } from "./etopo-sounding/index.js";
import { crossSectionTool } from "./cross-section/index.js";
import { contoursTool } from "./contours/index.js";
import { changeDetectionTool } from "./change-detection/index.js";
import { volumeCalculationTool } from "./volume-calculation/index.js";
import { satelliteDerivedBathymetryTool } from "./satellite-derived-bathymetry/index.js";
import { imageGeneratorTool } from "./image-generator/index.js";
import { realtimeWeatherTool } from "./realtime-weather/index.js";
import { historicalWeatherTool } from "./historical-weather/index.js";
import { tidesTool } from "./tides/index.js";
import { bagS102ConverterTool } from "./bag-s102-converter/index.js";
import { qimeraCommandTool } from "./qimera-command/index.js";
import { fmgtControllerTool } from "./fmgt-controller/index.js";
import { pointCloudWorkspaceTool } from "./point-cloud-workspace/index.js";
import { hydrostackConsoleTool } from "./hydrostack-console/index.js";
import { ehydroTool } from "./ehydro/index.js";
import { encChartsTool } from "./enc-charts/index.js";
import { travelPlanningTool } from "./travel-planning/index.js";
import { waterClarityTool } from "./water-clarity/index.js";
import { faaAirTrafficTool, aisVesselTrafficTool } from "./live-traffic/index.js";
import { coastPilotTool } from "./coast-pilot/index.js";
import { coastalWebcamsTool } from "./coastal-webcams/index.js";
import { concertFinderTool } from "./concert-finder/index.js";
import {
  forestFiresTool,
  floodPlanModelTool,
  stormSurgeModelTool,
  openDriftTool,
  illegalFishingReportsTool,
  fieldLogTool,
  vesselLogTool,
  marineMammalLogTool,
  dataTransferTool,
  localNoticeTool,
  fisheriesInteractionTool,
  s57s101Tool,
} from "./maritime-ops/index.js";

const ENABLED_KEY = "hydrotools:enabled-tools:v2";
const ORDER_KEY = "hydrotools:tool-order:v2";
const DOCK_KEY = "hydrotools:tool-docks:v2";
const BUILDER_CATEGORY_OPEN_KEY = "hydrotools:builder-category-open:v2";
const DOCK_CATEGORY_OPEN_KEY = "hydrotools:dock-category-open:v2";
const DEFAULT_ENABLED_TOOL_IDS = ["global-layers", "overlay-manager", "hydrostack-console", "image-generator", "point-cloud-workspace", "realtime-weather", "historical-weather", "tides", "travel-planning", "concert-finder", "coast-pilot-explorer", "coastal-webcams", "faa-air-traffic", "ais-vessel-traffic", "line-planning", "linework", "projection-manager", "etopo-sounding", "satellite-derived-bathymetry", "cross-section", "contours", "change-detection", "volume-calculation"];
const LEFT_DOCK_TOOL_IDS = new Set(["global-layers"]);
const BOTTOM_OWNER_TOOL_IDS = new Set(["cross-section", "enc-charts", "tides", "realtime-weather"]);
const DEFAULT_BOTTOM_DOCK_TOOL_IDS = new Set([]);
const DEFAULT_TOOL_STATE_VERSION = 1;
const TOOL_CATEGORY_ORDER = [
  "Survey Planning",
  "Analysis",
  "Data & Layers",
  "Weather & Ocean",
  "Traffic & Operations",
  "Utilities",
];

const TOOL_DEFS = [
  {
    ...globalLayersTool,
    category: "Data & Layers",
    description: "Global stack of all layers grouped by category.",
  },
  {
    ...overlayManagerTool,
    category: "Data & Layers",
    description: "Add/import basemaps and overlays.",
  },
  {
    ...imageGeneratorTool,
    category: "Survey Planning",
    description: "Plan trips, frame marinas, and generate reports and exports for survey missions.",
  },
  {
    ...realtimeWeatherTool,
    category: "Weather & Ocean",
    description: "Load NOAA realtime weather radar and forecast WMS layers.",
  },
  {
    ...historicalWeatherTool,
    category: "Weather & Ocean",
    description: "Query historical weather at a point and render 365-day climatology charts.",
  },
  {
    ...tidesTool,
    category: "Weather & Ocean",
    description: "NOAA tide station lookup with observed, predicted, and high/low series.",
  },
  {
    ...bagS102ConverterTool,
    category: "Data & Layers",
    description: "Generate BAG to S-102 conversion commands for local GDAL + s100py execution.",
  },
  {
    ...qimeraCommandTool,
    category: "Data & Layers",
    description: "Build Qimera 2.8.2 qimera-command workflows as validated commands and JSON configs.",
  },
  {
    ...fmgtControllerTool,
    category: "Data & Layers",
    description: "Scaffold and orchestrate FMGT backscatter mosaic automation jobs from exported GSF inputs.",
  },
  {
    ...pointCloudWorkspaceTool,
    category: "Data & Layers",
    description: "Open local/server point cloud datasets and preview sampled points in an interactive 3D workspace.",
  },
  {
    ...hydrostackConsoleTool,
    category: "Data & Layers",
    description: "Hydrostack health, packet counters, and simulated sensor packet controls.",
  },
  {
    ...ehydroTool,
    category: "Data & Layers",
    description: "Quick-load USACE eHydro survey and channel framework feature layers.",
  },
  {
    ...encChartsTool,
    category: "Data & Layers",
    description: "Leaflet ENC Direct vector layers with viewport querying and ENC-focused styling.",
  },
  {
    ...travelPlanningTool,
    category: "Survey Planning",
    description: "Point-based per diem, nearest airports, and travel cost planning metrics.",
  },
  {
    ...waterClarityTool,
    category: "Survey Planning",
    description: "Historical inland lake clarity planning scaffold with source catalog and normalized schema.",
  },
  {
    ...coastPilotTool,
    category: "Survey Planning",
    description: "Map-click Coast Pilot excerpts and TripTik-style route guide generation.",
  },
  {
    ...coastalWebcamsTool,
    category: "Weather & Ocean",
    description: "Curated coastal webcam map with preview and watchlist pins.",
  },
  {
    ...concertFinderTool,
    category: "Utilities",
    description: "Ticketmaster-backed concert search by point/radius or current map view.",
  },
  {
    ...forestFiresTool,
    category: "Traffic & Operations",
    description: "NASA FIRMS wildfire hotspot layers.",
  },
  {
    ...floodPlanModelTool,
    category: "Traffic & Operations",
    description: "Flood planning sketch model with water-rise buffers.",
  },
  {
    ...stormSurgeModelTool,
    category: "Traffic & Operations",
    description: "Storm-surge route envelope planner.",
  },
  {
    ...openDriftTool,
    category: "Traffic & Operations",
    description: "First-order drift tracks for spills or floating objects.",
  },
  {
    ...illegalFishingReportsTool,
    category: "Traffic & Operations",
    description: "Illegal fishing incident capture and export.",
  },
  {
    ...fieldLogTool,
    category: "Traffic & Operations",
    description: "Field log generation with geotagged notes.",
  },
  {
    ...vesselLogTool,
    category: "Traffic & Operations",
    description: "Vessel fueling, maintenance, and runtime ledger.",
  },
  {
    ...marineMammalLogTool,
    category: "Traffic & Operations",
    description: "Marine mammal observation log with map points.",
  },
  {
    ...dataTransferTool,
    category: "Traffic & Operations",
    description: "Data transfer job planner (boat to office).",
  },
  {
    ...localNoticeTool,
    category: "Traffic & Operations",
    description: "Draft Local Notice to Mariners bulletins.",
  },
  {
    ...fisheriesInteractionTool,
    category: "Traffic & Operations",
    description: "Seasonal fisheries interaction zoning map.",
  },
  {
    ...s57s101Tool,
    category: "Data & Layers",
    description: "S-57/S-101 GeoJSON viewer and layer management.",
  },
  {
    ...faaAirTrafficTool,
    category: "Traffic & Operations",
    description: "Realtime air traffic with OpenSky polling and ADSB fallback.",
  },
  {
    ...aisVesselTrafficTool,
    category: "Traffic & Operations",
    description: "Realtime AIS vessel traffic from the local Docker-backed database.",
  },
  {
    ...linePlanningTool,
    category: "Survey Planning",
    description: "AOI/centerline planning workflow shell.",
  },
  {
    ...lineworkTool,
    category: "Data & Layers",
    description: "Import, manage, draw, and export vector linework layers.",
  },
  {
    ...projectionManagerTool,
    category: "Data & Layers",
    description: "Manage project CRS profiles and coordinate transforms.",
  },
  {
    ...etopoSoundingTool,
    category: "Analysis",
    description: "Click-to-sample depth/elevation soundings from ETOPO or BlueTopo.",
  },
  {
    ...satelliteDerivedBathymetryTool,
    category: "Analysis",
    description: "Calibrate shallow-water SDB regressions from multiband GeoTIFF imagery and sounding controls.",
  },
  {
    ...crossSectionTool,
    category: "Analysis",
    description: "Draw centerline, generate cross sections, sample ETOPO profiles.",
  },
  {
    ...contoursTool,
    category: "Analysis",
    description: "Draw AOI and generate ETOPO depth contours.",
  },
  {
    ...changeDetectionTool,
    category: "Analysis",
    description: "Analyze XYZ difference grids for shoal/scour change clusters.",
  },
  {
    ...volumeCalculationTool,
    category: "Analysis",
    description: "Compute cut/fill/net volumes between two aligned XYZ surfaces.",
  },
];

function readEnabledIds() {
  try {
    const raw = localStorage.getItem(ENABLED_KEY);
    if (!raw) return [...DEFAULT_ENABLED_TOOL_IDS];
    const ids = JSON.parse(raw);
    if (!Array.isArray(ids)) return [...DEFAULT_ENABLED_TOOL_IDS];
    const migrated = ids.includes("live-traffic")
      ? [...ids.filter((id) => id !== "live-traffic"), "faa-air-traffic", "ais-vessel-traffic"]
      : ids;
    const filtered = migrated.filter((id) => TOOL_DEFS.some((t) => t.id === id));
    if (!filtered.includes("global-layers")) filtered.unshift("global-layers");
    return filtered;
  } catch {
    return [...DEFAULT_ENABLED_TOOL_IDS];
  }
}

function saveEnabledIds(ids) {
  localStorage.setItem(ENABLED_KEY, JSON.stringify(ids));
}

function readToolOrder() {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
}

function saveToolOrder(ids) {
  try {
    localStorage.setItem(ORDER_KEY, JSON.stringify(ids));
  } catch {}
}

function defaultDockForTool(id) {
  if (DEFAULT_BOTTOM_DOCK_TOOL_IDS.has(id)) return "bottom";
  return LEFT_DOCK_TOOL_IDS.has(id) ? "left" : "right";
}

function readDockAssignments() {
  try {
    const raw = localStorage.getItem(DOCK_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object") return {};
    const out = {};
    for (const def of TOOL_DEFS) {
      const v = String(parsed[def.id] || "").trim().toLowerCase();
      out[def.id] = ["left", "right", "bottom"].includes(v) ? v : defaultDockForTool(def.id);
    }
    return out;
  } catch {
    const out = {};
    for (const def of TOOL_DEFS) out[def.id] = defaultDockForTool(def.id);
    return out;
  }
}

function saveDockAssignments(assignments) {
  try { localStorage.setItem(DOCK_KEY, JSON.stringify(assignments || {})); } catch {}
}

function readBuilderCategoryOpenState() {
  const out = {};
  TOOL_CATEGORY_ORDER.forEach((category) => {
    out[category] = false;
  });
  return out;
}

function saveBuilderCategoryOpenState(state) {
  try { localStorage.setItem(BUILDER_CATEGORY_OPEN_KEY, JSON.stringify(state || {})); } catch {}
}

function readDockCategoryOpenState() {
  // Always start with dock categories collapsed on app load/reload.
  // Tool enabled/disabled state is persisted separately and remains intact.
  const out = {};
  ["left", "right", "bottom"].forEach((dock) => {
    out[dock] = {};
    TOOL_CATEGORY_ORDER.forEach((category) => {
      out[dock][category] = false;
    });
  });
  return out;
}

function saveDockCategoryOpenState(state) {
  try { localStorage.setItem(DOCK_CATEGORY_OPEN_KEY, JSON.stringify(state || {})); } catch {}
}

export function loadTools(appContext) {
  const allById = new Map(TOOL_DEFS.map((t) => [t.id, t]));
  const enabled = new Set(readEnabledIds());
  let toolOrder = readToolOrder();
  let dockAssignments = readDockAssignments();
  const active = new Set();
  const openPanels = new Set(); // defaults empty => collapsed on load
  let hasBottomDockHost = false;
  let builderButtonEl = null;
  let builderMenuOpen = false;
  let builderMenuScrollTop = 0;
  let builderFilterText = "";
  let builderFilterSelectionStart = 0;
  let builderFilterSelectionEnd = 0;
  let builderCategoryOpen = readBuilderCategoryOpenState();
  let dockCategoryOpen = readDockCategoryOpenState();
  let suppressSummaryToggleUntil = 0;

  function escapeBuilderText(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }

  function closeBuilderMenu() {
    const body = document.querySelector("#dashboard-builder-popover .dashboard-builder-popover-body");
    if (body) builderMenuScrollTop = body.scrollTop || 0;
    builderMenuOpen = false;
    const overlay = document.getElementById("dashboard-builder-overlay");
    if (overlay?.parentElement) overlay.parentElement.removeChild(overlay);
  }

  function renderBuilderMenu() {
    if (!builderMenuOpen) {
      closeBuilderMenu();
      return;
    }
    const existingBody = document.querySelector("#dashboard-builder-popover .dashboard-builder-popover-body");
    if (existingBody) builderMenuScrollTop = existingBody.scrollTop || 0;
    const tools = listAllTools();
    const filterNeedle = String(builderFilterText || "").trim().toLowerCase();
    const filteredTools = tools.filter((tool) => {
      if (!filterNeedle) return true;
      const haystack = [tool.name, tool.category, tool.description].join(" ").toLowerCase();
      return haystack.includes(filterNeedle);
    });
    const groupedTools = new Map(TOOL_CATEGORY_ORDER.map((category) => [category, []]));
    filteredTools.forEach((tool) => {
      if (!groupedTools.has(tool.category)) groupedTools.set(tool.category, []);
      groupedTools.get(tool.category).push(tool);
    });
    const chooserRows = [...groupedTools.entries()]
      .filter(([, items]) => items.length)
      .map(([category, items]) => `
        <details class="toolset-group dashboard-builder-category" data-builder-category="${escapeBuilderText(category)}" ${builderCategoryOpen[category] ? "open" : ""}>
          <summary>${category} <span class="small">(${items.length})</span></summary>
          <div class="toolset-content">
            ${items.map((tool) => `
              <label class="toolset-row">
                <div class="toolset-meta">
                  <div class="toolset-name">${tool.name}</div>
                  <div class="small">${tool.description}</div>
                </div>
                <input type="checkbox" data-tool-toggle="${tool.id}" ${tool.enabled ? "checked" : ""} />
              </label>
            `).join("")}
          </div>
        </details>
      `)
      .join("");
    let overlay = document.getElementById("dashboard-builder-overlay");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = "dashboard-builder-overlay";
      overlay.className = "settings-backdrop";
      overlay.setAttribute("aria-hidden", "false");
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) closeBuilderMenu();
      });
      document.getElementById("app")?.appendChild(overlay);
    }
    overlay.setAttribute("aria-hidden", "false");
    overlay.innerHTML = `
      <div class="settings-modal dashboard-builder-modal" role="dialog" aria-modal="true" aria-labelledby="dashboard-builder-title">
        <div class="settings-head">
          <div id="dashboard-builder-title" class="settings-title">Dashboard Builder</div>
          <button type="button" class="iconbtn" id="dashboard-builder-close" title="Close">x</button>
        </div>
        <div class="dashboard-builder-filter">
          <input
            id="dashboard-builder-filter"
            type="text"
            placeholder="Filter dashboards by name"
            value="${escapeBuilderText(builderFilterText)}"
            autocomplete="off"
          />
        </div>
        <div id="dashboard-builder-popover" class="dashboard-builder-popover">
          <div class="dashboard-builder-popover-body">${chooserRows || `<div class="small" style="padding:6px 2px;">No dashboards match that filter.</div>`}</div>
        </div>
      </div>
    `;
    const menu = overlay.querySelector("#dashboard-builder-popover");
    overlay.querySelector("#dashboard-builder-close")?.addEventListener("click", closeBuilderMenu);
    overlay.querySelector("#dashboard-builder-filter")?.addEventListener("input", (e) => {
      builderFilterText = e.target?.value || "";
      builderFilterSelectionStart = e.target?.selectionStart ?? builderFilterText.length;
      builderFilterSelectionEnd = e.target?.selectionEnd ?? builderFilterText.length;
      builderMenuScrollTop = 0;
      renderBuilderMenu();
    });
    menu.onchange = (e) => {
      const input = e.target;
      const id = input?.dataset?.toolToggle;
      if (!id) return;
      setToolEnabled(id, !!input.checked);
    };
    menu.querySelectorAll("details[data-builder-category]").forEach((details) => {
      details.addEventListener("toggle", () => {
        const category = String(details.dataset.builderCategory || "");
        if (!category) return;
        builderCategoryOpen[category] = details.open;
        saveBuilderCategoryOpenState(builderCategoryOpen);
      });
    });
    const body = overlay.querySelector(".dashboard-builder-popover-body");
    if (body) {
      body.scrollTop = builderMenuScrollTop;
      body.addEventListener("scroll", () => {
        builderMenuScrollTop = body.scrollTop || 0;
      }, { passive: true });
    }
    const filterInput = overlay.querySelector("#dashboard-builder-filter");
    if (filterInput) {
      filterInput.focus();
      const nextStart = Math.max(0, Math.min(builderFilterSelectionStart, filterInput.value.length));
      const nextEnd = Math.max(nextStart, Math.min(builderFilterSelectionEnd, filterInput.value.length));
      filterInput.setSelectionRange(nextStart, nextEnd);
    }
  }

  function ensureBuilderTopbarButton() {
    if (builderButtonEl) return;
    const wrap = document.querySelector(".brand-wrap");
    if (!wrap) return;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.id = "dashboard-builder-btn";
    btn.className = "btn";
    btn.textContent = "Dashboard Builder";
    btn.addEventListener("click", () => {
      builderMenuOpen = !builderMenuOpen;
      renderBuilderMenu();
    });
    wrap.appendChild(btn);
    builderButtonEl = btn;
    document.addEventListener("click", (e) => {
      if (!builderMenuOpen) return;
      const menu = document.getElementById("dashboard-builder-popover");
      const btn = document.getElementById("dashboard-builder-btn");
      const t = e.target;
      if (menu?.contains(t) || btn?.contains(t)) return;
      closeBuilderMenu();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeBuilderMenu();
    });
  }

  function normalizeToolOrder() {
    const valid = TOOL_DEFS.map((t) => t.id);
    const filtered = toolOrder.filter((id) => valid.includes(id));
    valid.forEach((id) => {
      if (!filtered.includes(id)) filtered.push(id);
    });
    toolOrder = filtered;
    saveToolOrder(toolOrder);
  }

  function normalizeDockAssignments() {
    const next = {};
    for (const def of TOOL_DEFS) {
      const v = String(dockAssignments?.[def.id] || "").trim().toLowerCase();
      next[def.id] = ["left", "right", "bottom"].includes(v) ? v : defaultDockForTool(def.id);
    }
    dockAssignments = next;
    saveDockAssignments(dockAssignments);
  }

  function resolveRequestedDock(id) {
    return dockAssignments[id] || defaultDockForTool(id);
  }

  function resolveEffectiveDock(id) {
    const requested = resolveRequestedDock(id);
    if (requested === "bottom" && BOTTOM_OWNER_TOOL_IDS.has(id)) return "right";
    return requested;
  }

  function inferDockForNewTool(id) {
    const def = allById.get(id);
    if (!def?.category) return defaultDockForTool(id);
    const categoryDocks = TOOL_DEFS
      .filter((tool) => tool.id !== id && tool.category === def.category && enabled.has(tool.id))
      .map((tool) => resolveEffectiveDock(tool.id));
    const sideDocks = [...new Set(categoryDocks.filter((dock) => dock === "left" || dock === "right"))];
    if (sideDocks.length === 1) return sideDocks[0];
    const allDocks = [...new Set(categoryDocks)];
    return allDocks.length === 1 ? allDocks[0] : defaultDockForTool(id);
  }

  function reconcileDockAssignmentOnEnable(id) {
    const inferredDock = inferDockForNewTool(id);
    const currentDock = dockAssignments[id];
    const defaultDock = defaultDockForTool(id);
    if (!currentDock) {
      dockAssignments[id] = inferredDock;
      saveDockAssignments(dockAssignments);
      return;
    }
    if (currentDock === inferredDock) return;
    // Preserve tools with a non-side default dock (for example Image Generator
    // in the bottom dock) when they are re-enabled from the builder.
    if (!["left", "right"].includes(defaultDock) && currentDock === defaultDock) return;
    if (currentDock === defaultDock && inferredDock !== defaultDock) {
      dockAssignments[id] = inferredDock;
      saveDockAssignments(dockAssignments);
    }
  }

  function listAllTools() {
    return TOOL_DEFS.map(({ id, name, category, description }) => ({
      id,
      name,
      category,
      description,
      enabled: enabled.has(id),
      dock: resolveEffectiveDock(id),
    }));
  }

  function persistEnabled() {
    saveEnabledIds(TOOL_DEFS.map((t) => t.id).filter((id) => enabled.has(id)));
  }

  function captureOpenPanels() {
    const roots = [document.getElementById("toolset-sidebar"), document.getElementById("layers-sidebar"), document.getElementById("bottom-toolset-sidebar")].filter(Boolean);
    if (!roots.length) return;
    openPanels.clear();
    roots.forEach((root) => {
      root.querySelectorAll("details[data-panel-id]").forEach((d) => {
        const id = d.dataset.panelId;
        if (!id || !id.startsWith("tool:")) return;
        if (d.open) openPanels.add(id);
      });
    });
  }

  function enableTool(id) {
    if (active.has(id)) return true;
    const tool = allById.get(id);
    if (!tool) return false;
    try {
      tool.init?.(appContext);
      active.add(id);
      appContext.log(`Tool loaded: ${id}`);
      return true;
    } catch (e) {
      console.error(`[Tool:${id}] init failed`, e);
      appContext.ui.toast(`Tool failed: ${tool.name}`);
      return false;
    }
  }

  function disableTool(id) {
    if (!active.has(id)) return;
    const tool = allById.get(id);
    try {
      tool?.destroy?.();
    } catch (e) {
      console.error(`[Tool:${id}] destroy failed`, e);
    }
    try {
      appContext.services.layers?.removeLayersByTool?.(id);
    } catch (e) {
      console.error(`[Tool:${id}] layer cleanup failed`, e);
    }
    active.delete(id);
    appContext.log(`Tool unloaded: ${id}`);
  }

  function setToolEnabled(id, shouldEnable, { silent = false } = {}) {
    if (!allById.has(id)) return false;
    const next = !!shouldEnable;
    const isEnabled = enabled.has(id);
    if (next === isEnabled) return true;
    captureOpenPanels();

    if (next) {
      const ok = enableTool(id);
      if (!ok) return false;
      enabled.add(id);
      reconcileDockAssignmentOnEnable(id);
      if (!toolOrder.includes(id)) {
        toolOrder.push(id);
        saveToolOrder(toolOrder);
      }
      if (!silent) appContext.ui.toast(`Enabled: ${allById.get(id).name}`);
    } else {
      disableTool(id);
      enabled.delete(id);
      if (!silent) appContext.ui.toast(`Disabled: ${allById.get(id).name}`);
    }

    persistEnabled();
    renderSidebar();
    appContext.events.emit("tools:changed", { tools: listAllTools() });
    return true;
  }

  function exportProjectData(options = {}) {
    const toolStates = {};
    const toolStateMeta = {};
    for (const def of TOOL_DEFS) {
      if (!enabled.has(def.id)) continue;
      if (!def.serialize) continue;
      try {
        const state = def.serialize(appContext, options);
        if (state !== undefined) {
          toolStates[def.id] = state;
          toolStateMeta[def.id] = {
            version: Number.isInteger(def.stateVersion) ? def.stateVersion : DEFAULT_TOOL_STATE_VERSION,
          };
        }
      } catch (e) {
        console.error(`[Tool:${def.id}] serialize failed`, e);
      }
    }
    return {
      enabledToolIds: TOOL_DEFS.map((t) => t.id).filter((id) => enabled.has(id)),
      toolOrder: [...toolOrder],
      toolStates,
      toolStateMeta,
    };
  }

  function importProjectData(data = {}, options = {}) {
    const report = {
      restored: 0,
      unresolved: [],
      errors: [],
      byTool: {},
    };
    const wanted = new Set(Array.isArray(data.enabledToolIds) ? data.enabledToolIds : []);
    wanted.add("global-layers");
    TOOL_DEFS.forEach((def) => {
      const shouldEnable = wanted.has(def.id);
      const isEnabled = enabled.has(def.id);
      if (shouldEnable !== isEnabled) setToolEnabled(def.id, shouldEnable, { silent: true });
    });

    if (Array.isArray(data.toolOrder) && data.toolOrder.length) {
      toolOrder = data.toolOrder.slice();
      normalizeToolOrder();
      renderSidebar();
    }

    const states = data.toolStates && typeof data.toolStates === "object" ? data.toolStates : {};
    const stateMeta = data.toolStateMeta && typeof data.toolStateMeta === "object" ? data.toolStateMeta : {};
    Object.entries(states).forEach(([id, state]) => {
      const def = allById.get(id);
      if (!def?.restore) return;
      if (!enabled.has(id)) return;
      try {
        const fromVersion = Number(stateMeta?.[id]?.version);
        const toVersion = Number.isInteger(def.stateVersion) ? def.stateVersion : DEFAULT_TOOL_STATE_VERSION;
        const migrated = def.migrateState
          ? def.migrateState(state, {
              fromVersion: Number.isInteger(fromVersion) ? fromVersion : DEFAULT_TOOL_STATE_VERSION,
              toVersion,
            })
          : state;
        const toolReport = def.restore(appContext, migrated, options) || {};
        const restored = Number(toolReport.restored || 0);
        const unresolved = Array.isArray(toolReport.unresolved) ? toolReport.unresolved : [];
        const errors = Array.isArray(toolReport.errors) ? toolReport.errors : [];
        report.byTool[id] = {
          restored,
          unresolved,
          errors,
        };
        report.restored += restored;
        report.unresolved.push(...unresolved.map((u) => ({ ...u, toolId: u.toolId || id })));
        report.errors.push(...errors.map((e) => ({ ...e, toolId: e.toolId || id })));
      } catch (e) {
        console.error(`[Tool:${id}] restore failed`, e);
        report.errors.push({ toolId: id, message: e?.message || String(e) });
      }
    });
    return report;
  }

  async function resolveProjectReference(ref, file) {
    if (!ref || typeof ref !== "object") return { ok: false, message: "Invalid reference" };
    const toolId = String(ref.toolId || "");
    const referenceId = String(ref.referenceId || "");
    if (!toolId || !referenceId) return { ok: false, message: "Missing reference info" };
    const def = allById.get(toolId);
    if (!def?.resolveReference) return { ok: false, message: `Tool ${toolId} cannot relink references` };
    try {
      const out = await def.resolveReference(appContext, referenceId, file, ref);
      return out && typeof out === "object" ? { ok: true, ...out } : { ok: true };
    } catch (e) {
      console.error(`[Tool:${toolId}] resolve reference failed`, e);
      return { ok: false, message: e?.message || String(e) };
    }
  }

  function renderSidebar() {
    ensureBuilderTopbarButton();
    const tools = listAllTools();
    const enabledTools = tools.filter((t) => t.enabled);
    const orderIndex = new Map(toolOrder.map((id, idx) => [id, idx]));
    const sortedEnabled = enabledTools
      .slice()
      .sort((a, b) => (orderIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (orderIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER));
    const leftEnabled = sortedEnabled.filter((t) => resolveEffectiveDock(t.id) === "left");
    const rightEnabled = sortedEnabled.filter((t) => resolveEffectiveDock(t.id) === "right");
    const bottomEnabled = sortedEnabled.filter((t) => resolveEffectiveDock(t.id) === "bottom");

    const toolCardMarkup = (tool) => `
      <details class="toolset-group" data-tool-id="${tool.id}" data-panel-id="tool:${tool.id}" ${openPanels.has(`tool:${tool.id}`) ? "open" : ""}>
        <summary draggable="true" data-tool-drag-handle="1" data-tool-id="${tool.id}">${tool.name}</summary>
        <div class="toolset-content" id="tool-panel-${tool.id}"></div>
      </details>
    `;
    const toolDetails = (list, emptyMsg, dockName) => {
      if (!list.length) return `<div class="small">${emptyMsg}</div>`;
      const grouped = new Map(TOOL_CATEGORY_ORDER.map((category) => [category, []]));
      list.forEach((tool) => {
        if (!grouped.has(tool.category)) grouped.set(tool.category, []);
        grouped.get(tool.category).push(tool);
      });
      return [...grouped.entries()]
        .filter(([, toolsInCategory]) => toolsInCategory.length)
        .map(([category, toolsInCategory]) => `
          <details
            class="toolset-group dock-category-group"
            data-dock-category="${category}"
            data-dock-name="${dockName}"
            data-panel-id="dock-category:${dockName}:${category}"
            ${dockCategoryOpen?.[dockName]?.[category] ? "open" : ""}
          >
            <summary draggable="true" data-category-drag-handle="1" data-dock-name="${dockName}" data-dock-category="${category}">${category} <span class="small">(${toolsInCategory.length})</span></summary>
            <div class="toolset-content dock-category-content">
              ${toolsInCategory.map((tool) => toolCardMarkup(tool)).join("")}
            </div>
          </details>
        `)
        .join("");
    };

    appContext.ui.mountLeftPanel(`
      <div id="layers-sidebar" data-dock-root="left">
        ${toolDetails(leftEnabled, "No enabled toolsets docked left.", "left")}
        <div class="tool-drop-end-zone" data-tool-drop-end="1" aria-hidden="true"></div>
      </div>
    `);

    appContext.ui.mountRightPanel(`
      <div id="toolset-sidebar" data-dock-root="right">
        ${toolDetails(rightEnabled, "No enabled toolsets docked right.", "right")}
        <div class="tool-drop-end-zone" data-tool-drop-end="1" aria-hidden="true"></div>
      </div>
    `);

    if (bottomEnabled.length) {
      appContext.ui.mountBottomPanel(`
        <div id="bottom-toolset-sidebar" data-dock-root="bottom">
          ${toolDetails(bottomEnabled, "No enabled toolsets docked bottom.", "bottom")}
          <div class="tool-drop-end-zone" data-tool-drop-end="1" aria-hidden="true"></div>
        </div>
      `, { ownerId: "dock-host", title: "Toolsets" });
      hasBottomDockHost = true;
    } else if (hasBottomDockHost) {
      appContext.ui.unmountBottomPanel("dock-host");
      hasBottomDockHost = false;
    }

    const leftRoot = document.getElementById("layers-sidebar");
    const rightRoot = document.getElementById("toolset-sidebar");
    const bottomRoot = document.getElementById("bottom-toolset-sidebar");
    const dockRoots = [leftRoot, rightRoot, bottomRoot].filter(Boolean);
    const dockRootByName = new Map(dockRoots.map((r) => [String(r.dataset?.dockRoot || ""), r]));

    dockRoots.forEach((root) => {
      root.addEventListener("click", (e) => {
        if (Date.now() >= suppressSummaryToggleUntil) return;
        const summary = e.target?.closest?.("summary");
        if (!summary) return;
        e.preventDefault();
        e.stopPropagation();
      }, true);
      root.addEventListener("toggle", (e) => {
        const details = e.target;
        if (!details?.matches?.("details[data-panel-id]")) return;
        const id = details.dataset.panelId;
        if (!id) return;
        if (id.startsWith("dock-category:")) {
          const [, dockName, ...categoryParts] = id.split(":");
          const category = categoryParts.join(":");
          if (!dockName || !category) return;
          if (!dockCategoryOpen[dockName]) dockCategoryOpen[dockName] = {};
          dockCategoryOpen[dockName][category] = details.open;
          saveDockCategoryOpenState(dockCategoryOpen);
          return;
        }
        if (details.open) openPanels.add(id);
        else openPanels.delete(id);
      });
    });

    let draggingToolId = null;
    let draggingCategory = null;
    let dragSourceEl = null;
    let dragPlaceholderEl = null;
    let dragGhostEl = null;
    let dropVisualState = null;
    const ensurePlaceholder = () => {
      if (dragPlaceholderEl) return dragPlaceholderEl;
      const el = document.createElement("div");
      el.className = "tool-drag-placeholder tool-drag-placeholder-hidden";
      dragPlaceholderEl = el;
      return dragPlaceholderEl;
    };
    const clearDropHints = () => {
      dockRoots.forEach((root) => root.querySelectorAll(".tool-drag-over-before, .tool-drag-over-after").forEach((el) => {
        el.classList.remove("tool-drag-over-before", "tool-drag-over-after");
      }));
    };
    const clearDropVisualState = () => {
      dropVisualState = null;
    };
    const clearDragState = () => {
      clearDropHints();
      if (dragPlaceholderEl?.parentElement) dragPlaceholderEl.parentElement.removeChild(dragPlaceholderEl);
      if (dragSourceEl) {
        dragSourceEl.classList.remove("tool-dragging", "tool-drag-source-hidden", "tool-drag-source-collapsed");
      }
      if (dragGhostEl?.parentElement) dragGhostEl.parentElement.removeChild(dragGhostEl);
      dragGhostEl = null;
      dragPlaceholderEl = null;
      dragSourceEl = null;
      draggingToolId = null;
      draggingCategory = null;
      clearDropVisualState();
    };
    const placePlaceholder = (dockRoot, target = null, after = false) => {
      const ph = ensurePlaceholder();
      ph.dataset.dropDock = String(dockRoot?.dataset?.dockRoot || "");
      if (!dockRoot) return;
      if (target && target.matches?.("details[data-tool-id]")) {
        const alreadyBefore = !after && ph.nextElementSibling === target && ph.parentElement === target.parentElement;
        const alreadyAfter = after && ph.previousElementSibling === target && ph.parentElement === target.parentElement;
        if (alreadyBefore || alreadyAfter) return;
        if (after) target.insertAdjacentElement("afterend", ph);
        else target.insertAdjacentElement("beforebegin", ph);
        return;
      }
      const tail = [...dockRoot.querySelectorAll("details[data-tool-id]")].filter((d) => d !== dragSourceEl).pop();
      if (tail) {
        const alreadyAtTail = ph.previousElementSibling === tail && ph.parentElement === tail.parentElement;
        if (!alreadyAtTail) tail.insertAdjacentElement("afterend", ph);
      } else if (ph.parentElement !== dockRoot || ph !== dockRoot.lastElementChild) {
        dockRoot.appendChild(ph);
      }
    };
    const animateDockReflow = (roots, mutate) => {
      const rootList = [...new Set((Array.isArray(roots) ? roots : [roots]).filter(Boolean))];
      const firstRects = new Map();
      rootList.forEach((root) => {
        root.querySelectorAll("details[data-tool-id]").forEach((el) => {
          if (el === dragSourceEl) return;
          firstRects.set(el, el.getBoundingClientRect());
        });
      });
      mutate?.();
      rootList.forEach((root) => {
        root.querySelectorAll("details[data-tool-id]").forEach((el) => {
          if (el === dragSourceEl) return;
          const first = firstRects.get(el);
          if (!first) return;
          const last = el.getBoundingClientRect();
          const dx = first.left - last.left;
          const dy = first.top - last.top;
          if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
          el.style.transition = "none";
          el.style.transform = `translate(${dx}px, ${dy}px)`;
          void el.getBoundingClientRect();
          el.style.transition = "transform 180ms cubic-bezier(0.22, 1, 0.36, 1)";
          el.style.transform = "";
          el.addEventListener("transitionend", () => {
            el.style.transition = "";
          }, { once: true });
        });
      });
    };
    const setDropVisual = (dockRoot, target = null, after = false) => {
      const nextState = {
        dock: String(dockRoot?.dataset?.dockRoot || ""),
        targetId: target?.dataset?.toolId || "",
        after: !!after,
      };
      if (
        dropVisualState
        && dropVisualState.dock === nextState.dock
        && dropVisualState.targetId === nextState.targetId
        && dropVisualState.after === nextState.after
      ) return;
      const prevRoot = dropVisualState?.dock ? dockRootByName.get(dropVisualState.dock) : null;
      animateDockReflow([prevRoot, dockRoot], () => {
        clearDropHints();
        placePlaceholder(dockRoot, target, after);
      });
      dropVisualState = nextState;
    };

    const applyDrop = (dropDock, targetId = null, after = false) => {
      if (!draggingToolId || !allById.has(draggingToolId)) return;
      dockAssignments[draggingToolId] = dropDock;
      const next = toolOrder.filter((id) => id !== draggingToolId);
      if (targetId && targetId !== draggingToolId && next.includes(targetId)) {
        let at = next.indexOf(targetId);
        if (after) at += 1;
        next.splice(Math.max(0, Math.min(at, next.length)), 0, draggingToolId);
      } else {
        const lastInDock = next.map((id, idx) => ({ id, idx })).filter(({ id }) => resolveEffectiveDock(id) === dropDock).pop();
        const at = lastInDock ? (lastInDock.idx + 1) : next.length;
        next.splice(Math.max(0, Math.min(at, next.length)), 0, draggingToolId);
      }
      toolOrder = next;
      normalizeToolOrder();
      normalizeDockAssignments();
      saveDockAssignments(dockAssignments);
      captureOpenPanels();
      renderSidebar();
    };

    const applyCategoryDrop = (dropDock) => {
      if (!draggingCategory || !["left", "right"].includes(dropDock)) return;
      const { category, fromDock } = draggingCategory;
      if (!category || !fromDock || fromDock === dropDock) return;
      const categoryToolIds = sortedEnabled
        .filter((tool) => resolveEffectiveDock(tool.id) === fromDock && tool.category === category)
        .map((tool) => tool.id)
        .filter((id) => allById.has(id));
      if (!categoryToolIds.length) return;
      categoryToolIds.forEach((id) => {
        dockAssignments[id] = dropDock;
      });
      normalizeDockAssignments();
      saveDockAssignments(dockAssignments);
      captureOpenPanels();
      renderSidebar();
    };

    dockRoots.forEach((root) => {
      root.addEventListener("dragstart", (e) => {
        const categoryHandle = e.target?.closest?.('summary[data-category-drag-handle="1"]');
        if (categoryHandle) {
          const category = String(categoryHandle.dataset.dockCategory || "");
          const fromDock = String(categoryHandle.dataset.dockName || "");
          if (!category || !["left", "right"].includes(fromDock)) return;
          draggingCategory = { category, fromDock };
          dragSourceEl = categoryHandle.parentElement?.closest?.("details[data-dock-category]") || null;
          dragSourceEl?.classList?.add("tool-dragging");
          try {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", `category:${fromDock}:${category}`);
            e.dataTransfer.dropEffect = "move";
            if (dragSourceEl) {
              const rect = dragSourceEl.getBoundingClientRect();
              const ghost = dragSourceEl.cloneNode(true);
              ghost.classList.add("tool-drag-ghost");
              ghost.style.width = `${Math.round(rect.width)}px`;
              ghost.style.height = `${Math.round(rect.height)}px`;
              ghost.style.position = "fixed";
              ghost.style.left = "-10000px";
              ghost.style.top = "-10000px";
              ghost.style.pointerEvents = "none";
              document.body.appendChild(ghost);
              dragGhostEl = ghost;
              const offsetX = Math.max(8, Math.min(rect.width - 8, (e.clientX || rect.left) - rect.left));
              const offsetY = Math.max(8, Math.min(rect.height - 8, (e.clientY || rect.top) - rect.top));
              e.dataTransfer.setDragImage(ghost, offsetX, offsetY);
            }
          } catch {}
          requestAnimationFrame(() => {
            if (!dragSourceEl || !draggingCategory) return;
            dragSourceEl.classList.add("tool-drag-source-collapsed");
          });
          return;
        }
        const handle = e.target?.closest?.('summary[data-tool-drag-handle="1"]');
        if (!handle) return;
        draggingToolId = handle.dataset.toolId || handle.parentElement?.dataset?.toolId || null;
        if (!draggingToolId) return;
        const details = root.querySelector(`details[data-tool-id="${draggingToolId}"]`);
        dragSourceEl = details || null;
        dragSourceEl?.classList?.add("tool-dragging");
        try {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", draggingToolId);
          e.dataTransfer.dropEffect = "move";
          if (dragSourceEl) {
            const rect = dragSourceEl.getBoundingClientRect();
            const ghost = dragSourceEl.cloneNode(true);
            ghost.classList.add("tool-drag-ghost");
            ghost.style.width = `${Math.round(rect.width)}px`;
            ghost.style.height = `${Math.round(rect.height)}px`;
            ghost.style.position = "fixed";
            ghost.style.left = "-10000px";
            ghost.style.top = "-10000px";
            ghost.style.pointerEvents = "none";
            document.body.appendChild(ghost);
            dragGhostEl = ghost;
            const offsetX = Math.max(8, Math.min(rect.width - 8, (e.clientX || rect.left) - rect.left));
            const offsetY = Math.max(8, Math.min(rect.height - 8, (e.clientY || rect.top) - rect.top));
            e.dataTransfer.setDragImage(ghost, offsetX, offsetY);
          }
        } catch {}
        requestAnimationFrame(() => {
          if (!dragSourceEl || !draggingToolId) return;
          dragSourceEl.classList.add("tool-drag-source-collapsed");
        });
      });
      root.addEventListener("dragend", () => {
        suppressSummaryToggleUntil = Date.now() + 250;
        clearDragState();
      });
      root.addEventListener("dragover", (e) => {
        if (draggingCategory) {
          const dockRoot = e.target?.closest?.("[data-dock-root]");
          if (!dockRoot) return;
          const dropDock = String(dockRoot.dataset.dockRoot || "");
          if (!["left", "right"].includes(dropDock)) return;
          e.preventDefault();
          return;
        }
        if (!draggingToolId) return;
        const dockRoot = e.target?.closest?.("[data-dock-root]");
        const onEndZone = !!e.target?.closest?.('[data-tool-drop-end="1"]');
        if (!dockRoot) return;
        e.preventDefault();

        const cards = [...dockRoot.querySelectorAll("details[data-tool-id]")].filter((d) => d !== dragSourceEl);
        if (onEndZone) {
          if (!cards.length) {
            setDropVisual(dockRoot, null, false);
            return;
          }
          const tail = cards[cards.length - 1];
          setDropVisual(dockRoot, tail, true);
          return;
        }
        if (!cards.length) {
          setDropVisual(dockRoot, null, false);
          return;
        }

        let beforeCard = null;
        for (const card of cards) {
          const r = card.getBoundingClientRect();
          const mid = r.top + (r.height / 2);
          if (e.clientY < mid) {
            beforeCard = card;
            break;
          }
        }
        if (beforeCard) {
          setDropVisual(dockRoot, beforeCard, false);
        } else {
          const tail = cards[cards.length - 1];
          setDropVisual(dockRoot, tail, true);
        }
      });
      root.addEventListener("drop", (e) => {
        if (draggingCategory) {
          const dockRoot = e.target?.closest?.("[data-dock-root]");
          if (!dockRoot) return;
          const dropDock = String(dockRoot.dataset.dockRoot || "");
          if (!["left", "right"].includes(dropDock)) return;
          e.preventDefault();
          applyCategoryDrop(dropDock);
          return;
        }
        if (!draggingToolId) return;
        const dockRoot = e.target?.closest?.("[data-dock-root]");
        if (!dockRoot) return;
        const dropDock = dockRoot.dataset.dockRoot;
        if (!["left", "right", "bottom"].includes(dropDock)) return;
        e.preventDefault();
        if (dragPlaceholderEl?.parentElement === dockRoot) {
          const next = dragPlaceholderEl.nextElementSibling?.closest?.("details[data-tool-id]");
          const prev = dragPlaceholderEl.previousElementSibling?.closest?.("details[data-tool-id]");
          const nextId = next?.dataset?.toolId;
          const prevId = prev?.dataset?.toolId;
          if (nextId && nextId !== draggingToolId) {
            applyDrop(dropDock, nextId, false);
            return;
          }
          if (prevId && prevId !== draggingToolId) {
            applyDrop(dropDock, prevId, true);
            return;
          }
        }
        applyDrop(dropDock);
      });
      root.addEventListener("dragleave", () => {});
    });

    document.addEventListener("dragend", () => {
      suppressSummaryToggleUntil = Date.now() + 250;
    }, { once: true });

    for (const tool of sortedEnabled) {
      const panelEl = document.getElementById(`tool-panel-${tool.id}`);
      const def = allById.get(tool.id);
      if (!panelEl || !def?.renderPanel) continue;
      try {
        def.renderPanel(appContext, panelEl);
        if (panelEl.dataset.subsetsInitialized !== "1" && tool.id !== "global-layers") {
          panelEl.querySelectorAll("details").forEach((d) => {
            d.open = false;
          });
          panelEl.dataset.subsetsInitialized = "1";
        }
      } catch (e) {
        console.error(`[Tool:${tool.id}] renderPanel failed`, e);
        panelEl.innerHTML = `<div class="small">Failed to render ${tool.name}</div>`;
      }
    }
    renderBuilderMenu();
  }

  normalizeToolOrder();
  normalizeDockAssignments();
  for (const tool of TOOL_DEFS) {
    if (!enabled.has(tool.id)) continue;
    const ok = enableTool(tool.id);
    if (!ok) enabled.delete(tool.id);
  }
  persistEnabled();
  renderSidebar();

  async function importDroppedFiles(fileList) {
    const files = [...(fileList || [])].filter(Boolean);
    if (!files.length) return { handled: false, imported: 0 };
    const supportedVector = files.filter((file) => /\.(geojson|json|kml|kmz|gpx|dxf|zip|shp|dbf|prj|cpg)$/i.test(String(file?.name || "")));
    if (!supportedVector.length) return { handled: false, imported: 0 };
    if (!enabled.has("linework")) {
      const ok = setToolEnabled("linework", true, { silent: true });
      if (!ok) return { handled: false, imported: 0 };
    }
    const linework = allById.get("linework");
    if (!linework?.handleExternalFiles) return { handled: false, imported: 0 };
    appContext.ui.toast(`Dispatching ${supportedVector.length} file${supportedVector.length === 1 ? "" : "s"} to Linework...`);
    const result = await linework.handleExternalFiles(appContext, supportedVector);
    const imported = Number(result?.imported || 0);
    return { handled: imported > 0, imported, toolId: "linework", pendingSiblingLookup: result?.pendingSiblingLookup === true };
  }

  return {
    listAllTools,
    isEnabled: (id) => enabled.has(id),
    setToolEnabled,
    renderSidebar,
    exportProjectData,
    importProjectData,
    importDroppedFiles,
    resolveProjectReference,
    destroy() {
      closeBuilderMenu();
      if (builderButtonEl?.parentElement) builderButtonEl.parentElement.removeChild(builderButtonEl);
      builderButtonEl = null;
      for (const id of [...active]) disableTool(id);
    },
  };
}
